import React, { useState } from 'react';
import { Mail, X, Plus, Trash2, Send, Clock, Calendar, CheckCircle } from 'lucide-react';
import { api } from '../services/api';
import { useToast } from './Toast';

// Generate send-time options in 30-min increments with readable labels
const TIME_OPTIONS = (() => {
    const opts = [];
    for (let h = 0; h < 24; h++) {
        for (let m of [0, 30]) {
            const hh = String(h).padStart(2, '0');
            const mm = String(m).padStart(2, '0');
            const value = `${hh}:${mm}`;
            const period = h < 12 ? 'AM' : 'PM';
            const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
            const label = `${displayH}:${mm} ${period}`;
            opts.push({ value, label });
        }
    }
    return opts;
})();

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_SHORT = { Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed', Thursday: 'Thu', Friday: 'Fri', Saturday: 'Sat', Sunday: 'Sun' };

const EmailSettings = ({ project, onClose, onSave }) => {
    const { showToast } = useToast();
    const [recipients, setRecipients] = useState(project.emailNotifications || []);
    const [schedule, setSchedule]     = useState(project.emailSchedule || []);
    const [emailTime, setEmailTime]   = useState(project.emailTime || '08:00');
    const [newEmail, setNewEmail]     = useState('');
    const [isSending, setIsSending]   = useState(false);
    const [isSaving, setIsSaving]     = useState(false);

    const addEmail = () => {
        const trimmed = newEmail.trim().toLowerCase();
        if (!trimmed || !trimmed.includes('@')) return showToast('Enter a valid email address', 'error');
        if (recipients.includes(trimmed)) return showToast('Already added', 'error');
        setRecipients([...recipients, trimmed]);
        setNewEmail('');
    };

    const toggleDay = (day) =>
        setSchedule(s => s.includes(day) ? s.filter(d => d !== day) : [...s, day]);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await onSave(recipients, schedule, emailTime);
            showToast('Settings saved');
        } catch(e) {
            showToast('Save failed', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSendNow = async () => {
        if (!recipients.length) return showToast('Add at least one recipient first', 'error');
        setIsSending(true);
        try {
            // Persist current settings first so server has latest recipients
            await api.syncProject(project.id, {
                ...project,
                emailNotifications: recipients,
                emailSchedule: schedule,
                emailTime,
            });
            await api.sendProjectReport(project.id);
            showToast('Report sent!');
        } catch(err) {
            showToast('Send failed: ' + err.message, 'error');
        } finally {
            setIsSending(false);
        }
    };

    // Friendly summary of the schedule
    const scheduleSummary = () => {
        if (!schedule.length) return 'No days selected — automatic sending disabled';
        const timeLabel = TIME_OPTIONS.find(o => o.value === emailTime)?.label || emailTime;
        const dayList = DAYS.filter(d => schedule.includes(d)).map(d => DAY_SHORT[d]).join(', ');
        return `Sends every ${dayList} at ${timeLabel}`;
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-xl max-w-md w-full shadow-2xl flex flex-col max-h-[90vh]">

                {/* Header */}
                <div className="flex justify-between items-center p-6 pb-4 border-b">
                    <h3 className="text-xl font-bold flex items-center gap-2">
                        <Mail className="text-purple-600" /> Email Settings
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={22} /></button>
                </div>

                <div className="overflow-y-auto flex-1 p-6 space-y-6">

                    {/* Recipients */}
                    <div>
                        <label className="text-sm font-bold text-gray-700 block mb-2">Recipients</label>
                        <div className="flex gap-2 mb-2">
                            <input
                                className="flex-1 border rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                                placeholder="name@example.com"
                                value={newEmail}
                                onChange={e => setNewEmail(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && addEmail()}
                            />
                            <button onClick={addEmail}
                                className="bg-purple-600 text-white px-3 rounded-lg hover:bg-purple-700 flex items-center gap-1">
                                <Plus size={16} />
                            </button>
                        </div>
                        <div className="space-y-1 max-h-28 overflow-y-auto">
                            {recipients.map(email => (
                                <div key={email} className="flex justify-between items-center bg-purple-50 px-3 py-1.5 rounded-lg text-sm">
                                    <span className="text-gray-700">{email}</span>
                                    <button onClick={() => setRecipients(r => r.filter(e => e !== email))}
                                        className="text-red-400 hover:text-red-600 ml-2"><Trash2 size={13} /></button>
                                </div>
                            ))}
                            {!recipients.length && (
                                <p className="text-xs text-gray-400 italic px-1">No recipients — add one above</p>
                            )}
                        </div>
                    </div>

                    {/* Schedule — days */}
                    <div>
                        <label className="text-sm font-bold text-gray-700 block mb-2 flex items-center gap-1.5">
                            <Calendar size={14} /> Send Days
                        </label>
                        <div className="grid grid-cols-7 gap-1">
                            {DAYS.map(day => (
                                <button key={day} onClick={() => toggleDay(day)}
                                    title={day}
                                    className={`py-2 rounded-lg text-xs font-bold transition-colors border ${
                                        schedule.includes(day)
                                            ? 'bg-purple-600 text-white border-purple-600'
                                            : 'bg-white text-gray-500 border-gray-200 hover:border-purple-300'
                                    }`}>
                                    {DAY_SHORT[day]}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Send time — dropdown */}
                    <div>
                        <label className="text-sm font-bold text-gray-700 block mb-2 flex items-center gap-1.5">
                            <Clock size={14} /> Send Time
                        </label>
                        <select
                            className="w-full border rounded-lg p-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                            value={emailTime}
                            onChange={e => setEmailTime(e.target.value)}
                        >
                            {TIME_OPTIONS.map(({ value, label }) => (
                                <option key={value} value={value}>{label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Summary */}
                    <div className={`flex items-start gap-2 rounded-lg p-3 text-xs ${
                        schedule.length && recipients.length
                            ? 'bg-green-50 border border-green-200 text-green-700'
                            : 'bg-gray-50 border border-gray-200 text-gray-500'
                    }`}>
                        <CheckCircle size={14} className="flex-shrink-0 mt-0.5" />
                        <span>{scheduleSummary()}</span>
                    </div>

                </div>

                {/* Footer */}
                <div className="flex justify-between gap-3 p-6 pt-4 border-t">
                    <button onClick={handleSendNow} disabled={isSending}
                        className="px-4 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg font-bold hover:bg-blue-100 flex items-center gap-2 disabled:opacity-50 text-sm">
                        <Send size={15} /> {isSending ? 'Sending...' : 'Send Now'}
                    </button>
                    <div className="flex gap-2">
                        <button onClick={onClose} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Cancel</button>
                        <button onClick={handleSave} disabled={isSaving}
                            className="px-6 py-2 bg-purple-600 text-white rounded-lg font-bold hover:bg-purple-700 disabled:opacity-50 text-sm">
                            {isSaving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmailSettings;
